import { useEffect, useId, useRef } from 'react';
import { ModalProps } from '@msc-b2c/interfaces/types';
import { clearModalTextSelection } from '@msc-b2c/shared-utils';
import './modal.module.scss';
import { Icon } from '../icons/Icon';
import { Text } from '../text/Text';
import clsx from 'clsx';

/**
 * Stack of currently open modals. Only the most recently opened modal
 * enforces the focus trap, so a modal opened on top of another takes the
 * trap over and gives it back when it closes. Without this, two mounted
 * modals would fight over focus in an infinite focusin loop.
 */
const modalStack: HTMLElement[] = [];

export const Modal = ({
  id,
  children,
  styleOption,
  onClose,
  headerTitle,
  modalIcon,
  shouldRenderBackdrop = false,
  iconStyleOption,
  hideCloseIcon = false,
  modalIconAriaLabel = '',
  ariaLabelledBy,
  ariaDescribedBy,
  initialFocusRef,
  headerStyleOption,
}: ModalProps) => {
  /// logic for modal to be autofocused when visible
  const modalRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const titleId = ariaLabelledBy ?? (headerTitle ? `${baseId}-title` : undefined);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const getFocusableElements = () =>
      Array.from(modalElement.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
        (el) =>
          !el.hasAttribute('disabled') &&
          el.getAttribute('aria-hidden') !== 'true' &&
          el.offsetParent !== null
      );

    const getPriorityFocusableElement = () => {
      const prioritySelectors = [
        '.itineraryBoxContainer__modal__footer__btn [href]',
        '.itineraryBoxContainer__modal__footer__btn button:not([disabled])',
        '.itineraryBoxContainer__modal__footer [href]',
        '.itineraryBoxContainer__modal__footer button:not([disabled])',
        '[class*="modal__footer__btn"] [href]',
        '[class*="modal__footer__btn"] button:not([disabled])',
        '[class*="modal__footer"] [href]',
        '[class*="modal__footer"] button:not([disabled])',
      ].join(',');

      const priorityElement = modalElement.querySelector<HTMLElement>(prioritySelectors);

      if (
        priorityElement &&
        !priorityElement.hasAttribute('disabled') &&
        priorityElement.getAttribute('aria-hidden') !== 'true' &&
        priorityElement.offsetParent !== null
      ) {
        return priorityElement;
      }

      return null;
    };

    const previouslyFocusedElement = document.activeElement as HTMLElement | null;

    let focusAttempts = 0;
    const maxFocusAttempts = 10;

    const focusPreferredElement = () => {
      const priorityElement = getPriorityFocusableElement();

      if (priorityElement) {
        priorityElement.focus();
        return;
      }

      const focusableElements = getFocusableElements();

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
        return;
      }
      focusAttempts += 1;

      if (focusAttempts < maxFocusAttempts) {
        globalThis.setTimeout(focusPreferredElement, 30);
      } else {
        modalElement.focus();
      }
    };

    requestAnimationFrame(() => {
      globalThis.setTimeout(focusPreferredElement, 0);
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.key === 'Escape') {
        onClose?.();
        return;
      }

      const isActivationKey = event.key === ' ' || event.key === 'Enter';

      if (activeElement && modalElement.contains(activeElement) && isActivationKey) {
        const isNativeButton = activeElement.tagName === 'BUTTON';
        const isLink = activeElement.tagName === 'A';
        const isAriaButton = activeElement.getAttribute('role') === 'button';

        if (!isNativeButton && (isLink || isAriaButton)) {
          const anchor = activeElement as HTMLAnchorElement;
          const href = anchor.href;
          const target = anchor.target;

          event.preventDefault();

          activeElement.click();

          /// find the href inside children and trigger it with the button
          if (href) {
            setTimeout(() => {
              if (target === '_blank') {
                globalThis.open(href, '_blank', 'noopener,noreferrer');
              } else {
                globalThis.location.assign(href);
              }
            }, 0);
          }

          return;
        }
      }

      if (event.key !== 'Tab') return;

      const updatedFocusableElements = getFocusableElements();

      if (updatedFocusableElements.length === 0) {
        event.preventDefault();
        modalElement.focus();
        return;
      }

      const firstElement = updatedFocusableElements[0];
      const lastElement = updatedFocusableElements[updatedFocusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement || document.activeElement === modalElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    modalElement.addEventListener('keydown', handleKeyDown);

    return () => {
      modalElement.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [onClose]);

  useEffect(() => {
    const container = modalRef.current;
    if (!container) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    modalStack.push(container);

    clearModalTextSelection();

    if (initialFocusRef?.current) {
      initialFocusRef.current.focus({ preventScroll: true });
    } else if (initialFocusRef) {
      container.focus({ preventScroll: true });
    } else {
      const labelledByElement = ariaLabelledBy ? document.getElementById(ariaLabelledBy) : null;
      const headerTitleElement =
        !labelledByElement && headerTitle && titleId ? document.getElementById(titleId) : null;
      const focusTarget = labelledByElement ?? headerTitleElement;

      if (focusTarget instanceof HTMLElement) {
        if (!focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
      } else {
        container.focus({ preventScroll: true });
      }
    }

    // Lock background scrolling while the modal is open. If the drawer already
    // owns scroll lock via body.-overlay, do not override inline overflow to
    // avoid lock/unlock interference when this top modal closes.
    const shouldManageBodyOverflow = !document.body.classList.contains('-overlay');
    const previousBodyOverflow = document.body.style.overflow;
    if (shouldManageBodyOverflow) {
      document.body.style.overflow = 'hidden';
    }

    // Focus trap: if focus escapes the modal (Tab past the last element or
    // Shift+Tab before the first), pull it back to the modal container so
    // the next Tab continues from its first focusable child.
    const keepFocusInside = (event: FocusEvent) => {
      const isTopMostModal = modalStack[modalStack.length - 1] === container;
      if (!isTopMostModal || container.contains(event.target as Node)) {
        return;
      }
      // Only reclaim focus that actually left from inside this modal
      // (relatedTarget is the element that lost focus); never steal focus
      // that was already elsewhere, e.g. inside another modal.
      if (!container.contains(event.relatedTarget as Node)) {
        return;
      }
      container.focus({ preventScroll: true });
    };

    document.addEventListener('focusin', keepFocusInside);

    return () => {
      document.removeEventListener('focusin', keepFocusInside);
      const index = modalStack.lastIndexOf(container);
      if (index !== -1) {
        modalStack.splice(index, 1);
      }
      if (shouldManageBodyOverflow) {
        document.body.style.overflow = previousBodyOverflow;
      }
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [ariaLabelledBy, headerTitle, titleId, initialFocusRef]);

  return (
    <>
      {shouldRenderBackdrop && (
        <div
          className={'cmp-container__modal-backdrop'}
          data-backdrop-open={true}
          aria-hidden="true"
        />
      )}
      <div
        ref={modalRef}
        id={id}
        className={clsx('cmp-container__modal --react-modal', styleOption)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headerTitle ? `${id}-title` : undefined}
        tabIndex={-1}
      >
        {headerTitle ? (
          <>
            <div className={'cmp-container__modal-header-wrapper'}>
              <div className={'cmp-container__modal-header'}>
                <Text
                  id={headerTitle}
                  tagHtml={'h3'}
                  text={headerTitle}
                  styleOption={headerStyleOption}
                />
                {shouldRenderBackdrop && (
                  <Icon
                    icon={modalIcon ?? ''}
                    onClick={onClose}
                    ariaLabel={modalIconAriaLabel}
                    styleOption={iconStyleOption}
                  />
                )}
              </div>
            </div>
            <div className="cmp-container__modal-body">
              <div className={'cmp-container'}>{children}</div>
            </div>
          </>
        ) : (
          <div className="cmp-container__modal-body">
            {!hideCloseIcon && (
              <Icon
                icon={modalIcon ?? 'close'}
                onClick={onClose}
                styleOption={iconStyleOption}
                ariaLabel={modalIconAriaLabel}
              />
            )}
            <div className={'cmp-container'}>{children}</div>
          </div>
        )}
      </div>
    </>
  );
};
